import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Calendar,
  Download,
  FileText,
  Minus,
  X,
  Clock,
  Edit,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import api from "~/services/api";
import { useNavigate } from "react-router-dom";

const monthColumns = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
];

const subColumns = ["Vacation Days", "Absent Days", "Sick Days", "Total"];
const getMonthIndexFromKey = (monthKey) => {
  const index = Number(monthKey.slice(5, 7)) - 1;
  return Number.isNaN(index) ? -1 : index;
};

const getDaysInMonthByKey = (monthKey) => {
  const monthIndex = getMonthIndexFromKey(monthKey);
  const year = Number(monthKey.slice(0, 4));
  if (monthIndex < 0 || Number.isNaN(year)) return 0;
  return new Date(year, monthIndex + 1, 0).getDate();
};

const getWeekdayNameByKey = (monthKey, day) => {
  const monthIndex = getMonthIndexFromKey(monthKey);
  const year = Number(monthKey.slice(0, 4));
  const date = new Date(year, monthIndex, day);
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return weekdays[date.getDay()];
};

const getWeekdayFull = (date) => {
  const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  return weekdays[date.getDay()];
};

const isWeekendByKey = (monthKey, day) => {
  const monthIndex = getMonthIndexFromKey(monthKey);
  const year = Number(monthKey.slice(0, 4));
  const date = new Date(year, monthIndex, day);
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
};

const isWeekendDate = (date) => {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
};

const ROWS_PER_PAGE = 10;

const getMonthKey = (month, year) => {
  const monthIndex = monthColumns.indexOf(month);
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
};

const formatMonthLabel = (monthKey) => {
  const monthIndex = getMonthIndexFromKey(monthKey);
  const monthName = monthColumns[monthIndex] || monthKey;
  const year = monthKey.slice(0, 4);
  return `${monthName} ${year}`;
};

const getMonthKeysInRange = (fromMonth, toMonth, year) => {
  const fromIdx = monthColumns.indexOf(fromMonth);
  const toIdx = monthColumns.indexOf(toMonth);
  const start = Math.min(fromIdx, toIdx);
  const end = Math.max(fromIdx, toIdx);
  return monthColumns
    .slice(start, end + 1)
    .map((month) => getMonthKey(month, year));
};

const getPeriodFromTo = (fromMonth, toMonth, year) => {
  const fromKey = getMonthKey(fromMonth, year);
  const toKey = getMonthKey(toMonth, year);
  const fromDate = `${fromKey}-01`;
  const toDate = `${toKey}-${String(getDaysInMonthByKey(toKey)).padStart(2, "0")}`;
  return { fromDate, toDate };
};

const buildDailyTotals = (hoursByDate = {}, monthKey, daysInMonth) => {
  const totals = Array(daysInMonth).fill(0);
  Object.entries(hoursByDate).forEach(([date, entries]) => {
    if (!date.startsWith(monthKey)) return;
    const day = Number(date.slice(8, 10));
    if (Number.isNaN(day) || day < 1 || day > daysInMonth) return;
    const entryList = entries || [];
    const hasSickDay = entryList.some(
      (entry) =>
        (entry?.type || "").toLowerCase() === "sickday" ||
        (entry?.type || "").toLowerCase() === "sick day"
    );
    const hasVacation = entryList.some(
      (entry) => (entry?.type || "").toLowerCase() === "vacation"
    );
    if (hasSickDay) {
      totals[day - 1] = "S";
      return;
    }
    if (hasVacation) {
      totals[day - 1] = "V";
      return;
    }
    const totalHours = entryList.reduce(
      (sum, entry) => sum + (Number(entry?.hours) || 0),
      0
    );
    totals[day - 1] = totalHours;
  });
  return totals;
};

const buildAdjustmentTotals = (adjustments = [], monthKey) => {
  return adjustments.reduce((acc, adjustment) => {
    if (!adjustment || !monthKey) return acc;
    const adjustmentDate = adjustment?.date || "";
    if (adjustmentDate && !adjustmentDate.startsWith(monthKey)) {
      return acc;
    }
    return acc + (Number(adjustment?.hours) || 0);
  }, 0);
};

function TimesheetModal({
  isOpen,
  onClose,
  employeeName,
  timesheetId,
  monthKey,
  monthLabel,
  unit,
  onEdit,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const tableContainerRef = useRef(null);
  const [modalEntries, setModalEntries] = useState([]);
  const [modalError, setModalError] = useState("");
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [useShiftColumns, setUseShiftColumns] = useState(false);

  const entries = useMemo(() => {
    return modalEntries;
  }, [modalEntries]);

  const totalPages = Math.max(1, Math.ceil(entries.length / ROWS_PER_PAGE));
  const startIdx = (currentPage - 1) * ROWS_PER_PAGE;
  const endIdx = startIdx + ROWS_PER_PAGE;
  const currentEntries = entries.slice(startIdx, endIdx);
  const startRecord = entries.length ? startIdx + 1 : 0;
  const endRecord = Math.min(endIdx, entries.length);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen, monthKey]);

  useEffect(() => {
    let isActive = true;

    async function loadTimesheetDetails() {
      if (!isOpen) return;
      if (!timesheetId) {
        setModalEntries([]);
        setModalError("No timesheet selected.");
        setUseShiftColumns(false);
        return;
      }
      setLoadingDetails(true);
      setModalError("");
      try {
        const { data } = await api.get(`/timesheets/${timesheetId}`);
        if (!isActive) return;
        const hasShiftColumns = (data?.entries || []).some(
          (entry) =>
            entry?.morning_from ||
            entry?.morning_to ||
            entry?.afternoon_from ||
            entry?.afternoon_to ||
            entry?.evening_from ||
            entry?.evening_to
        );
        setUseShiftColumns(hasShiftColumns);
        const formatted = (data?.entries || []).map((entry, index) => {
          const date = entry?.date;
          const parsedDate = date ? new Date(`${date}T00:00:00`) : null;
          const displayDate = date
            ? `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`
            : "--/--/----";
          const entryType = entry?.entry_type || "work";
          const isWeekendDay = parsedDate ? isWeekendDate(parsedDate) : false;
          return {
            id: entry?.id || index,
            weekday: parsedDate ? getWeekdayFull(parsedDate) : "--",
            date: displayDate,
            from: entry?.time_from ? entry.time_from.slice(0, 5) : "--:--",
            to: entry?.time_to ? entry.time_to.slice(0, 5) : "--:--",
            morning_from: entry?.morning_from
              ? entry.morning_from.slice(0, 5)
              : "--:--",
            morning_to: entry?.morning_to ? entry.morning_to.slice(0, 5) : "--:--",
            afternoon_from: entry?.afternoon_from
              ? entry.afternoon_from.slice(0, 5)
              : "--:--",
            afternoon_to: entry?.afternoon_to
              ? entry.afternoon_to.slice(0, 5)
              : "--:--",
            evening_from: entry?.evening_from
              ? entry.evening_from.slice(0, 5)
              : "--:--",
            evening_to: entry?.evening_to
              ? entry.evening_to.slice(0, 5)
              : "--:--",
            type: entryType === "adjustment" ? "Adjustment" : "Work",
            description: entry?.description || "",
            hours: `${entry?.hours || 0}${unit === "hours" ? "h" : ` ${unit}`}`,
            isWeekend: isWeekendDay,
          };
        });
        setModalEntries(formatted);
      } catch (err) {
        if (!isActive) return;
        setModalEntries([]);
        setModalError(
          err?.response?.data?.error || "Failed to load timesheet details"
        );
        setUseShiftColumns(false);
      } finally {
        if (isActive) {
          setLoadingDetails(false);
        }
      }
    }

    loadTimesheetDetails();

    return () => {
      isActive = false;
    };
  }, [isOpen, timesheetId, unit]);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,.75)" }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800">Timesheet Details</h2>
            <p className="text-sm text-gray-600 mt-1">
              {employeeName || "-"} • {monthLabel}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {entries.length > 0 && (
                <>
                  Showing <span className="font-semibold">{startRecord}</span> to{" "}
                  <span className="font-semibold">{endRecord}</span> of{" "}
                  <span className="font-semibold">{entries.length}</span> rows
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Page</span>
              <select
                value={currentPage}
                onChange={(e) => handlePageChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                disabled={totalPages <= 1}
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <option key={page} value={page}>
                    {page}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-600">of {totalPages}</span>
            </div>
          </div>
          {modalError && (
            <div className="text-xs text-red-600 mt-2">{modalError}</div>
          )}
          {loadingDetails && (
            <div className="text-xs text-gray-500 mt-2">Loading details...</div>
          )}
        </div>

        <div ref={tableContainerRef} className="flex-1 overflow-auto min-h-0">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-gray-100 z-10 shadow-sm">
              <tr className="border-b-2 border-gray-300">
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Weekday</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Date</th>
                {useShiftColumns ? (
                  <>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Morning From</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Morning To</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Afternoon From</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Afternoon To</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Evening From</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Evening To</th>
                  </>
                ) : (
                  <>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">From</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">To</th>
                  </>
                )}
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Type</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Description</th>
                <th className="text-right p-3 text-sm font-semibold text-gray-700">Hours</th>
              </tr>
            </thead>
            <tbody>
              {currentEntries.map((entry, idx) => (
                <tr
                  key={entry.id}
                  className={`border-b transition-colors ${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                  } hover:bg-orange-50`}
                >
                  <td
                    className={`p-3 text-sm font-semibold ${
                      entry.isWeekend ? "text-red-500" : "text-gray-700"
                    }`}
                  >
                    {entry.weekday}
                  </td>
                  <td className="p-3 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      {entry.date}
                      <Calendar size={14} className="text-gray-400" />
                    </div>
                  </td>
                  {useShiftColumns ? (
                    <>
                      <td className="p-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {entry.morning_from}
                          <Clock size={14} className="text-gray-400" />
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {entry.morning_to}
                          <Clock size={14} className="text-gray-400" />
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {entry.afternoon_from}
                          <Clock size={14} className="text-gray-400" />
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {entry.afternoon_to}
                          <Clock size={14} className="text-gray-400" />
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {entry.evening_from}
                          <Clock size={14} className="text-gray-400" />
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {entry.evening_to}
                          <Clock size={14} className="text-gray-400" />
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {entry.from}
                          <Clock size={14} className="text-gray-400" />
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {entry.to}
                          <Clock size={14} className="text-gray-400" />
                        </div>
                      </td>
                    </>
                  )}
                  <td className="p-3 text-sm text-gray-600">{entry.type}</td>
                  <td className="p-3 text-sm text-gray-500 italic">{entry.description}</td>
                  <td
                    className={`p-3 text-sm text-right font-semibold ${
                      entry.isWeekend ? "text-gray-400" : "text-orange-500"
                    }`}
                  >
                    {entry.hours}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50 flex-shrink-0">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm px-3 font-medium text-gray-700">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded transition-colors"
            >
              Close
            </button>
            <button
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors flex items-center gap-2"
              onClick={() => onEdit(timesheetId)}
              disabled={!timesheetId}
            >
              <Edit size={14} />
              Edit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TimesheetRow({
  row,
  rowKey,
  visibleMonths = [],
  expandedMonths = [],
  dailyData = {},
  onCellClick = () => {},
}) {
  return (
    <tr className="bg-white hover:bg-gray-50">
      <td className="px-3 py-2 text-xs font-medium sticky left-0 z-10 bg-inherit border border-gray-300">
        {row.label}
      </td>
      {visibleMonths.map((month, monthIdx) => {
        const monthData = row.values?.[month] || {
          vacation: 0,
          absent: 0,
          sick: 0,
          total: 0,
        };
        const isLastMonth = monthIdx === visibleMonths.length - 1;
        const isMonthExpanded = expandedMonths.includes(month);
        const daysInMonth = getDaysInMonthByKey(month);
        const dailyHours = dailyData[`${rowKey}-${month}`] || [];
        const monthAdjustments = row.adjustmentsByMonth?.[month] || 0;

        return (
          <React.Fragment key={`${rowKey}-${month}`}>
            <td
              className="px-2 py-2 text-xs text-center border-t border-b border-r border-gray-300 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
              onClick={() => onCellClick(row, month, "vacation")}
            >
              {monthData.vacation}
            </td>
            <td
              className="px-2 py-2 text-xs text-center border-t border-b border-r border-gray-300 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
              onClick={() => onCellClick(row, month, "absent")}
            >
              {monthData.absent}
            </td>
            <td
              className="px-2 py-2 text-xs text-center border-t border-b border-r border-gray-300 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
              onClick={() => onCellClick(row, month, "sick")}
            >
              {monthData.sick}
            </td>
            <td
              className="px-2 py-2 text-xs text-center border-t border-b border-r border-gray-300 font-semibold hover:bg-blue-50 cursor-pointer transition-colors duration-150"
              onClick={() => onCellClick(row, month, "total")}
            >
              {monthData.total}
            </td>
            {isMonthExpanded && (
              <>
                <td
                  className="px-2 py-2 text-xs text-center border-t border-b border-r border-blue-200 bg-blue-50 hover:bg-blue-100 text-gray-700 cursor-pointer transition-colors duration-150 font-semibold min-w-[72px]"
                  onClick={() => onCellClick(row, month, "adjustment")}
                >
                  {monthAdjustments}
                </td>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                  const value = dailyHours[day - 1];
                  const weekend = isWeekendByKey(month, day);
                  const displayValue =
                    typeof value === "string" ? value : value > 0 ? value : "";
                  const isSickDay = displayValue === "S";
                  const isVacationDay = displayValue === "V";

                  return (
                    <React.Fragment key={`${rowKey}-${month}-day-fragment-${day}`}>
                      <td
                        className={`px-1 py-2 text-xs text-center border-t border-b border-r border-gray-300 min-w-[40px] cursor-pointer transition-colors duration-150 ${
                          weekend
                            ? "bg-gray-200 hover:bg-gray-300"
                            : "bg-cyan-50 hover:bg-cyan-100"
                        } ${
                          isSickDay
                            ? "text-green-600 font-bold"
                            : isVacationDay
                            ? "text-blue-600 font-bold"
                            : ""
                        }`}
                        onClick={() => onCellClick(row, month, "day", day)}
                      >
                        {displayValue}
                      </td>
                    </React.Fragment>
                  );
                })}
              </>
            )}
            {!isLastMonth && (
              <td className="w-[3px] bg-gray-600 p-0 border-0"></td>
            )}
          </React.Fragment>
        );
      })}
    </tr>
  );
}

export default function TimesheetReport() {
  const navigate = useNavigate();
  const [periodType, setPeriodType] = useState("specific");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("JANUARY");
  const [fromMonth, setFromMonth] = useState("JANUARY");
  const [toMonth, setToMonth] = useState("MARCH");
  const [year, setYear] = useState(2025);
  const [expandedMonths, setExpandedMonths] = useState([]);
  const [dailyData, setDailyData] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ employee: null, month: "" });
  const [reportRows, setReportRows] = useState([]);
  const [reportUnit, setReportUnit] = useState("hours");
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportMonths, setReportMonths] = useState([]);
  const [yearSummaryRows, setYearSummaryRows] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const selectedMonthKey = useMemo(
    () => getMonthKey(selectedMonth, year),
    [selectedMonth, year]
  );

  useEffect(() => {
    let isActive = true;
    async function loadDepartments() {
      try {
        const { data } = await api.get("/departments", {
          params: { limit: 999, page: 1, orderBy: "name", orderASC: "ASC" },
        });
        if (!isActive) return;
        const options = (data?.rows || data || []).map((dept) => ({
          value: dept.id,
          label: dept.filial?.name
            ? `${dept.name} - ${dept.filial.name}`
            : dept.name,
        }));
        setDepartmentOptions(options);
      } catch (err) {
        if (!isActive) return;
        console.error("Error loading departments:", err);
        setDepartmentOptions([]);
      }
    }
    loadDepartments();
    return () => {
      isActive = false;
    };
  }, []);

  const availableMonths = useMemo(() => {
    const yearPrefix = `${year}-`;
    return reportMonths.filter((month) => month.startsWith(yearPrefix)).sort();
  }, [reportMonths, year]);

  const fullYearMonths = useMemo(
    () => monthColumns.map((month) => getMonthKey(month, year)),
    [year]
  );

  const visibleMonths = useMemo(() => {
    switch (periodType) {
      case "specific":
        return [selectedMonthKey];
      case "custom": {
        return getMonthKeysInRange(fromMonth, toMonth, year);
      }
      case "allyear":
        return fullYearMonths;
      case "totalyear":
        return [];
      default:
        return [selectedMonthKey];
    }
  }, [
    periodType,
    selectedMonthKey,
    fromMonth,
    toMonth,
    year,
    availableMonths,
  ]);

  const yearlyTotals = useMemo(() => {
    const monthsForTotals =
      visibleMonths.length > 0 ? visibleMonths : fullYearMonths;
    return reportRows.map((row) => {
      const totals = monthsForTotals.reduce(
        (acc, monthKey) => {
          const monthData = row.values?.[monthKey] || {};
          acc.totalHours += Number(monthData.total) || 0;
          acc.vacation += Number(monthData.vacation) || 0;
          acc.absent += Number(monthData.absent) || 0;
          acc.sick += Number(monthData.sick) || 0;
          return acc;
        },
        { totalHours: 0, vacation: 0, absent: 0, sick: 0 }
      );
      return { ...row, yearlyTotals: totals };
    });
  }, [reportRows, visibleMonths, fullYearMonths]);

  useEffect(() => {
    let isActive = true;

    async function loadReport() {
      setLoadingReport(true);
      setReportError("");
      try {
        const departmentParam =
          departmentFilter && departmentFilter !== "all"
            ? { department_id: departmentFilter }
            : {};
        if (periodType === "totalyear") {
          const { data } = await api.get("/timesheets/year-summary", {
            params: { year, ...departmentParam },
          });
          console.log("Timesheet report year-summary payload:", data);

          if (!isActive) return;

          setReportUnit(data?.unit || "hours");
          setReportMonths([]);
          setReportRows([]);
          setYearSummaryRows(
            (data?.employees || []).map((employee) => ({
              label: employee?.name || "-",
              yearlyTotals: {
                totalHours: employee?.summary?.total || 0,
                vacation: employee?.summary?.vacationDays || 0,
                absent: employee?.summary?.absentDays || 0,
                sick: employee?.summary?.sickDays || 0,
              },
            }))
          );
          return;
        }

        const requestMonth =
          periodType === "specific" ? selectedMonthKey : undefined;
        const requestPeriod =
          periodType === "custom"
            ? getPeriodFromTo(fromMonth, toMonth, year)
            : null;
        const { data } = await api.get("/timesheets/monthly-summary", {
          params: requestMonth
            ? { month: requestMonth, ...departmentParam }
            : requestPeriod
            ? {
                from: requestPeriod.fromDate,
                to: requestPeriod.toDate,
                ...departmentParam,
              }
            : { ...departmentParam },
        });
        console.log("Timesheet report monthly-summary payload:", data);

        if (!isActive) return;

        setReportUnit(data?.unit || "hours");
        setYearSummaryRows([]);

        const monthsData = data?.months || [];
        setReportMonths(monthsData.map((month) => month.month).filter(Boolean));

        const employeeMap = new Map();
        monthsData.forEach((monthEntry) => {
          const monthKey = monthEntry?.month;
          if (!monthKey) return;
          (monthEntry?.employees || []).forEach((employee) => {
            const name = employee?.name || "-";
            if (!employeeMap.has(name)) {
              employeeMap.set(name, {
                label: name,
                values: {},
                hoursByMonth: {},
                adjustmentsByMonth: {},
              });
            }
            const row = employeeMap.get(name);
            row.values[monthKey] = {
              vacation: employee?.summary?.vacationDays || 0,
              absent: employee?.summary?.absentDays || 0,
              sick: employee?.summary?.sickDays || 0,
              total: employee?.summary?.total || 0,
            };
            row.hoursByMonth[monthKey] = employee?.hoursByDate || {};
            row.adjustmentsByMonth[monthKey] = buildAdjustmentTotals(
              employee?.adjustments || [],
              monthKey
            );
          });
        });

        setReportRows(Array.from(employeeMap.values()));
      } catch (err) {
        if (!isActive) return;
        setReportRows([]);
        setReportMonths([]);
        setYearSummaryRows([]);
        setReportError(
          err?.response?.data?.error || "Failed to load timesheet report"
        );
      } finally {
        if (isActive) {
          setLoadingReport(false);
        }
      }
    }

    loadReport();

    return () => {
      isActive = false;
    };
  }, [periodType, selectedMonthKey, fromMonth, toMonth, year, departmentFilter]);

  const toggleExpandMonth = (month) => {
    const isExpanding = !expandedMonths.includes(month);

    if (isExpanding) {
      const daysInMonth = getDaysInMonthByKey(month);
      const newDailyData = { ...dailyData };

      reportRows.forEach((row) => {
        newDailyData[`${row.label}-${month}`] = buildDailyTotals(
          row.hoursByMonth?.[month],
          month,
          daysInMonth
        );
      });

      setDailyData(newDailyData);
      setExpandedMonths((prev) => [...prev, month]);
    } else {
      setExpandedMonths((prev) => prev.filter((m) => m !== month));
    }
  };

  const getMonthColSpan = (month) => {
    const isExpanded = expandedMonths.includes(month);
    const baseSpan = 4;
    if (isExpanded) {
      return baseSpan + getDaysInMonthByKey(month) + 1;
    }
    return baseSpan;
  };

  const getTotalExtraColumns = () => {
    return Math.max(0, visibleMonths.length - 1);
  };

  const getTimesheetIdFromMonth = (employee, monthKey, day) => {
    const hoursByDate = employee?.hoursByMonth?.[monthKey] || {};
    if (day) {
      const dateKey = `${monthKey}-${String(day).padStart(2, "0")}`;
      const dayEntries = hoursByDate[dateKey] || [];
      return dayEntries.find((entry) => entry?.timesheetPeriodId)
        ?.timesheetPeriodId;
    }
    const dates = Object.keys(hoursByDate).sort();
    for (const date of dates) {
      const dateEntries = hoursByDate[date] || [];
      const found = dateEntries.find((entry) => entry?.timesheetPeriodId);
      if (found) return found.timesheetPeriodId;
    }
    return null;
  };

  const handleCellClick = (employee, month, _type, day) => {
    const timesheetId = getTimesheetIdFromMonth(employee, month, day);
    setModalData({ employee, month, timesheetId });
    setModalOpen(true);
  };

  const renderYearlyTable = () => (
    <table className="w-full min-w-max border-collapse">
      <thead className="sticky top-0 z-10">
        <tr>
          <th
            className="px-3 py-3 text-xs font-semibold text-left bg-gray-200 border border-gray-400 sticky left-0 z-20"
            rowSpan={2}
          >
            Name:
          </th>
          <th
            className="px-2 py-2 text-xs font-bold text-center bg-blue-400 border border-gray-400 text-white"
            colSpan={4}
          >
            YEARLY
          </th>
        </tr>
        <tr>
          <th className="px-2 py-2 text-[10px] font-semibold text-center bg-gray-100 border border-gray-300 w-20">
            Total Hours
          </th>
          <th className="px-2 py-2 text-[10px] font-semibold text-center bg-green-200 border border-gray-300 w-20">
            Vacation Days
          </th>
          <th className="px-2 py-2 text-[10px] font-semibold text-center bg-yellow-200 border border-gray-300 w-20">
            Absent Days
          </th>
          <th className="px-2 py-2 text-[10px] font-semibold text-center bg-red-200 border border-gray-300 w-20">
            Sick Days
          </th>
        </tr>
      </thead>
      <tbody>
        {(periodType === "totalyear" ? yearSummaryRows : yearlyTotals).length ===
        0 ? (
          <tr>
            <td
              className="px-2 py-4 text-xs text-gray-500 text-center border"
              colSpan={5}
            >
              No data for this selection
            </td>
          </tr>
        ) : (
          (periodType === "totalyear" ? yearSummaryRows : yearlyTotals).map(
            (row) => (
            <tr key={row.label} className="bg-white hover:bg-gray-50">
              <td className="px-3 py-2 text-xs font-medium sticky left-0 z-10 bg-inherit border border-gray-300">
                {row.label}
              </td>
              <td className="px-2 py-2 text-xs text-center border border-gray-300">
                {row.yearlyTotals.totalHours}
              </td>
              <td className="px-2 py-2 text-xs text-center border border-gray-300 bg-green-200">
                {row.yearlyTotals.vacation}
              </td>
              <td className="px-2 py-2 text-xs text-center border border-gray-300 bg-yellow-200">
                {row.yearlyTotals.absent}
              </td>
              <td className="px-2 py-2 text-xs text-center border border-gray-300 bg-red-200">
                {row.yearlyTotals.sick}
              </td>
            </tr>
          )
          )
        )}
      </tbody>
    </table>
  );

  const renderMonthlyTable = () => (
    <table className="w-full min-w-max border-collapse">
      <thead className="sticky top-0 z-10">
        <tr>
          <th
            className="px-3 py-3 text-xs font-semibold text-left bg-gray-200 border border-gray-400 sticky left-0 z-20"
            rowSpan={2}
          >
            Employee
          </th>
          {visibleMonths.map((month, monthIdx) => {
            const isLastMonth = monthIdx === visibleMonths.length - 1;
            const isMonthExpanded = expandedMonths.includes(month);
            const colSpan = getMonthColSpan(month);

            return (
              <React.Fragment key={month}>
                <th
                  className="px-2 py-2 text-xs font-bold text-center bg-gray-300 border-t border-b border-l border-r border-gray-400 relative"
                  colSpan={colSpan}
                >
                  {formatMonthLabel(month)}
                  <button
                    className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-orange-500 hover:bg-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg z-30"
                    onClick={() => toggleExpandMonth(month)}
                  >
                    {isMonthExpanded ? <Minus size={14} /> : "+"}
                  </button>
                </th>
                {!isLastMonth && (
                  <th
                    className="w-[3px] bg-gray-600 p-0 border-0"
                    rowSpan={2}
                  ></th>
                )}
              </React.Fragment>
            );
          })}
        </tr>
        <tr>
          {visibleMonths.map((month) => {
            const isMonthExpanded = expandedMonths.includes(month);
            const daysInMonth = getDaysInMonthByKey(month);

            return (
              <React.Fragment key={`${month}-sub`}>
                {subColumns.map((sub) => (
                  <th
                    key={`${month}-${sub}`}
                    className="px-2 py-2 text-[10px] font-semibold text-center bg-gray-200 border-b border-r border-gray-300 w-24"
                  >
                    {sub}
                  </th>
                ))}
                {isMonthExpanded && (
                  <>
                    <th className="px-2 py-2 text-[10px] font-semibold text-center bg-blue-50 text-gray-700 border-b border-r border-blue-200 min-w-[72px]">
                      Adj
                    </th>
                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(
                      (day) => {
                        const weekday = getWeekdayNameByKey(month, day);
                        const weekend = isWeekendByKey(month, day);
                        return (
                          <React.Fragment key={`${month}-day-fragment-${day}`}>
                            <th
                              className={`px-1 py-1 text-[10px] font-semibold text-center border-b border-r border-gray-300 min-w-[40px] ${
                                weekend ? "bg-gray-300" : "bg-cyan-200"
                              }`}
                            >
                              <div className="flex flex-col items-center gap-0.5">
                                <span
                                  className={`text-[9px] ${
                                    weekend
                                      ? "text-red-600 font-bold"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {weekday}
                                </span>
                                <span
                                  className={`text-xs font-bold ${
                                    weekend ? "text-red-600" : "text-gray-800"
                                  }`}
                                >
                                  {day}
                                </span>
                              </div>
                            </th>
                          </React.Fragment>
                        );
                      }
                    )}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {reportRows.length === 0 ? (
          <tr>
            <td
              className="px-2 py-4 text-xs text-gray-500 text-center border"
              colSpan={
                1 +
                visibleMonths.reduce((acc, m) => acc + getMonthColSpan(m), 0) +
                getTotalExtraColumns()
              }
            >
              No data for this selection
            </td>
          </tr>
        ) : (
          reportRows.map((row) => (
            <TimesheetRow
              key={row.label}
              row={row}
              rowKey={row.label}
              visibleMonths={visibleMonths}
              expandedMonths={expandedMonths}
              dailyData={dailyData}
              onCellClick={handleCellClick}
            />
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div className="w-full h-full flex flex-col bg-white border overflow-hidden rounded-xl">
      <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-gray-700" />
          <h2 className="text-lg font-bold text-gray-700">Timesheet Report</h2>
        </div>
        <button className="px-3 py-1.5 bg-orange-500 text-white rounded-sm text-xs flex items-center gap-1 hover:bg-orange-600">
          <Download size={14} /> Export
        </button>
      </div>

      <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Period Type</label>
          <select
            value={periodType}
            onChange={(e) => {
              setPeriodType(e.target.value);
              setExpandedMonths([]);
            }}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-orange-500 min-w-[150px]"
          >
            <option value="specific">Specific Month</option>
            <option value="custom">Custom Period</option>
            <option value="allyear">All Year</option>
            <option value="totalyear">Total Year</option>
          </select>
        </div>

        {periodType === "specific" && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setExpandedMonths([]);
              }}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-orange-500 min-w-[120px]"
            >
              {monthColumns.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </div>
        )}

        {periodType === "custom" && (
          <>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">From</label>
              <select
                value={fromMonth}
                onChange={(e) => setFromMonth(e.target.value)}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-orange-500 min-w-[120px]"
              >
                {monthColumns.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">To</label>
              <select
                value={toMonth}
                onChange={(e) => setToMonth(e.target.value)}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-orange-500 min-w-[120px]"
              >
                {monthColumns.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Department</label>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-orange-500 min-w-[150px]"
          >
            <option value="all">All Departments</option>
            {departmentOptions.map((department) => (
              <option key={department.value} value={department.value}>
                {department.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Year</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-orange-500"
          >
            {[2024, 2025, 2026].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
          <Calendar size={14} />
          <span>Year: {year}</span>
          {periodType === "specific" && (
            <span className="ml-2">| Month: {selectedMonth}</span>
          )}
          {periodType === "custom" && (
            <span className="ml-2">
              | Period: {fromMonth} to {toMonth}
            </span>
          )}
          {periodType === "allyear" && <span className="ml-2">| All Year (Monthly View)</span>}
          {periodType === "totalyear" && <span className="ml-2">| Total Year (Summary)</span>}
        </div>

        {reportError && (
          <div className="text-xs text-red-600 mb-2">{reportError}</div>
        )}
        {loadingReport && (
          <div className="text-xs text-gray-500 mb-2">Loading report...</div>
        )}

        <div className="overflow-x-auto mb-6">
          {periodType === "totalyear" ? renderYearlyTable() : renderMonthlyTable()}
        </div>
      </div>

      <TimesheetModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        employeeName={modalData.employee?.label}
        timesheetId={modalData.timesheetId}
        monthKey={modalData.month}
        monthLabel={modalData.month ? formatMonthLabel(modalData.month) : "-"}
        unit={reportUnit}
        onEdit={(id) => {
          if (!id) return;
          navigate(`/Administrative/HR/Timesheet/Create/${id}`);
        }}
      />
    </div>
  );
}
